import { MarketRadio, MarketRow } from '@market/react';
import { useTranslation } from 'react-i18next';
import styles from './LocationRow.module.css';

export enum LocationRowStatus {
  Offer = 'offer',
  CancellationPending = 'cancellationPending',
  ActiveLoan = 'activeLoan',
  ApplicationInReview = 'applicationInReview',
  NoOffer = 'noOffer',
  ActiveMcaGlobalLoan = 'activeMcaGlobalLoan',
  FicoPullStatusRequired = 'ficoPullStatusRequired',
}

export type LocationRowState =
  | {
    status: Exclude<LocationRowStatus, LocationRowStatus.Offer>;
  }
  | { status: LocationRowStatus.Offer; formattedFinancedAmount: string };

export interface LocationRowProps {
  readonly locationId: string;
  readonly locationLabel: string;
  readonly selectLocation: () => void;
  readonly state?: LocationRowState;
}

export const LocationRow = ({
  locationId,
  locationLabel,
  selectLocation,
  state,
}: LocationRowProps) => {
  const { t } = useTranslation();

  const getStatusText = (state: LocationRowState) => {
    switch (state.status) {
      case LocationRowStatus.CancellationPending:
        return t('bankingShared.locationSelector.cancelPending');
      case LocationRowStatus.ActiveLoan:
        return t('bankingShared.locationSelector.activeLoan');
      case LocationRowStatus.ActiveMcaGlobalLoan:
        return t('bankingShared.locationSelector.activeMcaGlobalLoan');
      case LocationRowStatus.Offer:
        return t('bankingShared.locationSelector.offer', {
          formattedFinancedAmount: state.formattedFinancedAmount,
        });
      case LocationRowStatus.ApplicationInReview:
        return t('bankingShared.locationSelector.financingRequestInReview');
      case LocationRowStatus.FicoPullStatusRequired:
        return t('bankingShared.locationSelector.boostRequired');
      case LocationRowStatus.NoOffer:
        return t('bankingShared.locationSelector.noOffer');
      default:
        return '';
    }
  };

  return (
    <MarketRow
      value={locationId}
      data-testid="search-result-row"
      onClick={selectLocation}
    >
      <div slot="label" data-testid="search-result-row-label">{locationLabel}</div>
      {state && (
        <div slot="subtext" data-testid="search-result-row-subtext">
          <span className={state.status === LocationRowStatus.Offer ? styles.offerText : undefined}>
            {getStatusText(state)}
          </span>
        </div>
      )}
      <MarketRadio slot="control" />
    </MarketRow>
  );
};
